import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import DataGrid, { Filter } from '#/common/components/DataGrid';
import MatchItemsModal from '#/common/components/MatchItemsModal';
import type {
  GetLocationListQuery,
  GetLocationListQueryVariables,
  Location,
} from '#/graphql/types';
import { gql, useQuery } from '@apollo/client';
import { getLocationList } from '#/graphql/queries';
import { createMatchLocationColumns } from './columns';
import { matchStatuses } from '../../../utils/constants';
import { actions } from '../LocationDataGrid/actions';
import useToast from '#/common/hooks/useToast';
import { Level } from '../Location.Mappings';
import MatchedLocationPreview from './MatchedLocationPreview';

const getLocationListQuery = gql(getLocationList);

export interface MatchLocationsModalProps {
  visible: boolean;
  location?: Location;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (selectedTisLocationId: string | null) => Promise<void> | void;
}

const MatchLocationsModal: React.FC<MatchLocationsModalProps> = ({
  visible,
  location,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { alert } = useToast();
  const selectedTisLocationIdRef = useRef<string | null>(null);
  const matchCandidatesGridRef = useRef<any>(null);

  const getSelectedLocationId = useCallback(() => selectedTisLocationIdRef.current, []);

  const isCurrentlyMatched =
    location?.matchStatus === matchStatuses.matched.key && !!location?.thirdPartyId;

  const { data: matchedTisLocationData } = useQuery<
    GetLocationListQuery,
    GetLocationListQueryVariables
  >(gql(getLocationList), {
    variables: {
      params: {
        filter: JSON.stringify([
          ['matchStatus', '=', matchStatuses.archived.key],
          'AND',
          ['thirdPartyId', '=', location?.thirdPartyId],
          'AND',
          ['tenantId', '=', location?.tenantId],
        ]),
        take: 1,
      },
    },
    skip: !isCurrentlyMatched,
  });

  const matchedTisLocation = matchedTisLocationData?.getLocationList?.data?.[0] ?? null;

  useEffect(() => {
    selectedTisLocationIdRef.current = matchedTisLocation?.id ?? null;
  }, [matchedTisLocation?.id]);

  useEffect(() => {
    if (!visible) {
      selectedTisLocationIdRef.current = matchedTisLocation?.id ?? null;
    }
  }, [visible, matchedTisLocation?.id]);

  const handleSelectLocationId = useCallback((id: string) => {
    selectedTisLocationIdRef.current = id;

    // Repaint rows so custom radio cells re-evaluate checked state without reloading data.
    matchCandidatesGridRef.current?.instance?.repaint?.();
  }, []);

  const handleRowClick = useCallback(
    (e: any) => {
      const rowId = e?.data?.id;
      if (rowId) {
        handleSelectLocationId(rowId);
      }
    },
    [handleSelectLocationId],
  );

  const matchCandidatesPermanentFilters = useMemo(() => {
    const filters: Filter[] = [
      ['thirdPartyId', 'isNotNull'],
      'AND',
      ['source', 'isNotNull'],
      'AND',
      ['level', '=', Level.Building],
      'AND',
      ['matchStatus', '=', matchStatuses.tis.key],
    ];

    if (location?.id) filters.push('AND', ['id', '<>', location.id]);

    if (location?.tenantId) filters.push('AND', ['tenantId', '=', location.tenantId]);

    return filters;
  }, [location?.id, location?.tenantId]);

  const matchCandidatesColumns = useMemo(
    () =>
      createMatchLocationColumns({
        getSelectedLocationId,
        onSelectLocationId: handleSelectLocationId,
      }),
    [getSelectedLocationId, handleSelectLocationId],
  );

  const handleConfirm = async () => {
    const selectedId = selectedTisLocationIdRef.current;

    if (!selectedId) {
      void alert('error', 'Please select a Location to match');
      return;
    }

    await onConfirm(selectedId);
  };

  const handleClose = () => {
    selectedTisLocationIdRef.current = null;
    onClose();
  };

  const handleClearSelection = () => {
    if (!location?.thirdPartyId) {
      void alert(
        'error',
        'The Location is not currently matched with any TC Location, so there is no selection to clear.',
      );
      return;
    }
    selectedTisLocationIdRef.current = null;
    void onConfirm(null);
  };

  return (
    <MatchItemsModal
      visible={visible}
      itemType="Location"
      item={location?.name || location?.id || '-'}
      isSubmitting={isSubmitting}
      warningMessage="All of the Location's data will be updated after matching it with a new one."
      currentMatchPreview={
        matchedTisLocation ? <MatchedLocationPreview location={matchedTisLocation} /> : undefined
      }
      onSecondaryAction={handleClearSelection}
      onClose={handleClose}
      onConfirm={handleConfirm}>
      <DataGrid
        id="match-locations-candidates"
        pluralText="Locations"
        singularText="Location"
        selectionMode="none"
        columns={matchCandidatesColumns}
        getItemsQuery={getLocationListQuery}
        actions={actions}
        permanentFilters={matchCandidatesPermanentFilters}
        onRowClick={handleRowClick}
        disableStateStoring
        defaultPageSize={5}
        setRef={(ref) => {
          matchCandidatesGridRef.current = ref;
        }}
      />
    </MatchItemsModal>
  );
};

export default React.memo(MatchLocationsModal);
